import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowsUpDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import type {
  BookmarkSortField,
  BookmarkSortOrder,
} from '../../../../types/bookmark'
import { useTranslation } from 'react-i18next'

const SORT_FIELDS: BookmarkSortField[] = [
  'updatedAt',
  'createdAt',
  'clickCount',
  'title',
]

interface SortDropdownProps {
  sortBy: BookmarkSortField
  sortOrder: BookmarkSortOrder
  onSortChange: (sortBy: BookmarkSortField) => void
}

/** 정렬 선택 드롭다운. 같은 항목을 다시 고르면 오름/내림차순이 바뀐다. */
export default function SortDropdown({
  sortBy,
  sortOrder,
  onSortChange,
}: SortDropdownProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const currentLabel = t(`bookmark.sort.${sortBy}`)
  const label =
    sortOrder === 'desc'
      ? currentLabel
      : t('bookmark.sort.ascending', { label: currentLabel })

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
        className="flex min-w-[130px] items-center justify-between rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm transition hover:bg-gray-50 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
      >
        <span className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
          <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />
          {label}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            role="listbox"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700"
          >
            {SORT_FIELDS.map(field => {
              const isActive = field === sortBy
              return (
                <li key={field}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onSortChange(field)
                      setIsOpen(false)
                    }}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left transition hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-medium">
                        {t(`bookmark.sort.${field}`)}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {t(`bookmark.sort.${field}Desc`)}
                      </span>
                    </span>
                    {isActive && (
                      <span className="text-xs text-primary-600 dark:text-primary-400">
                        {sortOrder === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  )
}
